import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useFileManagerStore } from '../../store/useFileManagerStore';
import ContextMenu from '../UI/ContextMenu';
import FileDetailsView from './FileDetailsView';
import PreviewModal from '../Preview/PreviewModal';
import EditModal from '../Edit/EditModal';
import { DndContext, DragEndEvent, DragOverlay, useSensor, useSensors, PointerSensor } from '@dnd-kit/core';
import { FileItem } from './FileItem';
import { FileStat, FolderSizeResult } from '../../api/IFileSystem';
import { processDropItems } from '../../utils/fileUtils';
import { getBaseName, joinPath } from '../../utils/pathUtils';
import { t } from '../../i18n';
import { VirtualFileList } from './VirtualFileList';
import { useFolderSize } from '../../hooks/useFolderSize';
import { useDriveSize } from '../../hooks/useDriveSize';
import { PercentBar } from '../UI/PercentBar';
import { DriveCard } from './DriveCard';
import TreemapView from '../SpaceView/TreemapView';
import { useFileSearchStatusStore } from '../../store/useFileSearchStatus';
import { FileSearchStatus } from '../../../../types';

// Threshold for using virtual scrolling (files count)
const VIRTUAL_SCROLL_THRESHOLD = 50; // Lowered from 200 to reduce render overhead

// Rename input component - auto-selects text only on mount
interface RenameInputProps {
  defaultValue: string;
  className: string;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
}

const RenameInput: React.FC<RenameInputProps> = ({ defaultValue, className, onKeyDown, onBlur }) => {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, []); // Only run on mount

  return (
    <input
      ref={inputRef}
      defaultValue={defaultValue}
      className={className}
      onKeyDown={onKeyDown}
      onBlur={onBlur}
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
    />
  );
};

const MainView: React.FC = () => {
  const store = useFileManagerStore();

  const {
    files, isLoading, selectedPaths, setSelectedPaths, setCurrentPath, viewMode,
    renamingPath, cancelRenaming, endRenaming, openPreview, fs, refresh, uploadFiles, checkConflictsBatch,
    totalFiles, hasMore, currentPath, sortConfig
  } = store;

  const [contextMenu, setContextMenu] = useState<{ x: number, y: number, path?: string } | null>(null);
  const [activeDragFile, setActiveDragFile] = useState<FileStat | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  // Folder size hook - lazy loads folder sizes after file list renders
  const { sizes: folderSizes, currentFolderSize, fetchFolderSizes } = useFolderSize(currentPath, files);

  // Search engine status for "pending init" display in non-virtual list mode
  const searchEngineStatus = useFileSearchStatusStore((s) => s.status);
  const openSearchEngineInitModal = useFileManagerStore((s) => s.openSearchEngineInitModal);
  const isSearchEngineReady = searchEngineStatus === FileSearchStatus.READY;

  const getFileExt = (name: string) => {
    const parts = name.split('.');
    if (parts.length <= 1) return '';
    return parts[parts.length - 1].toLowerCase();
  };

  const sortedFiles = useMemo(() => {
    const isFolder = (file: FileStat) => file.type === 'dir' || file.type === 'drive';
    const getTypeSortValue = (file: FileStat) => {
      if (isFolder(file)) return 'folder';
      const ext = getFileExt(file.name);
      return ext || 'file';
    };
    const getSortValue = (file: FileStat) => {
      switch (sortConfig.key) {
        case 'name':
          return file.name.toLowerCase();
        case 'type':
          return getTypeSortValue(file);
        case 'size':
          if (file.type === 'dir' && folderSizes[file.path]) {
            return folderSizes[file.path].total_size;
          }
          return file.size;
        case 'updatedAt':
          return file.updatedAt;
        default:
          return file[sortConfig.key];
      }
    };
    return [...files].sort((a, b) => {
      const aValue = getSortValue(a);
      const bValue = getSortValue(b);
      const direction = sortConfig.direction === 'asc' ? 1 : -1;
      const aIsFolder = a.type === 'dir' || a.type === 'drive';
      const bIsFolder = b.type === 'dir' || b.type === 'drive';

      if (sortConfig.key === 'type') {
        if (aIsFolder !== bIsFolder) {
          return sortConfig.direction === 'desc'
            ? (aIsFolder ? -1 : 1)
            : (aIsFolder ? 1 : -1);
        }
        if (aValue < bValue) return -1 * direction;
        if (aValue > bValue) return 1 * direction;
        const nameCompare = a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
        return nameCompare * direction;
      }

      // Special handling for size sorting: folders and files are mixed if sorting by size
      if (sortConfig.key !== 'size') {
        if (a.type !== b.type) {
           if (a.type === 'dir') return -1;
           if (b.type === 'dir') return 1;
        }
      }

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      
      // Fallback to name sort for equal values
      return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    });
  }, [files, sortConfig, folderSizes]);

  // Drive size hook - for "This PC" view
  const { sizes: driveSizes } = useDriveSize(currentPath, files);

  // Check if we're in root (This PC) view
  const isRootView = currentPath === 'root';

  // Fetch folder sizes for visible folders (non-virtual mode)
  useEffect(() => {
    if (files.length > 0 && files.length <= VIRTUAL_SCROLL_THRESHOLD) {
      // For small directories, fetch sizes for all visible folders
      const folders = files.filter(f => f.type === 'dir');
      if (folders.length > 0) {
        fetchFolderSizes(folders);
      }
    }
  }, [files, fetchFolderSizes]);
  
  // Selection
  const [selectionBox, setSelectionBox] = useState<{ x: number, y: number, w: number, h: number } | null>(null);
  const selectionStartRef = useRef<{ x: number, y: number } | null>(null);
  const isDragSelectingRef = useRef(false);
  
  // Optimization Refs
  const itemsRectCache = useRef<{ path: string, rect: DOMRect }[]>([]);
  const rafRef = useRef<number | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 }
    })
  );

  // Render rename input - moved before early return to follow React hooks rules
  const renderRenameInput = useCallback((file: FileStat) => (
    <RenameInput
      defaultValue={file.name}
      className={`bg-white/10 border border-win-accent rounded px-1 outline-none ${viewMode === 'grid' ? 'text-xs text-center w-full' : 'text-sm'}`}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          endRenaming(file.path, e.currentTarget.value);
        } else if (e.key === 'Escape') {
          e.preventDefault();
          cancelRenaming();
        }
      }}
      onBlur={(e) => endRenaming(file.path, e.currentTarget.value)}
    />
  ), [viewMode, cancelRenaming, endRenaming]);

  // Native Drag & Drop Handlers (Upload)
  const handleNativeDragOver = (e: React.DragEvent) => {
      if (e.dataTransfer.types.includes('Files')) {
          e.preventDefault();
          e.stopPropagation();
          setIsDragOver(true);
      }
  };

  const handleNativeDragLeave = (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
  };

  const handleNativeDrop = async (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);

      const items = e.dataTransfer.items;
      if (items && items.length > 0) {
          const flatFiles = await processDropItems(items);
          uploadFiles(flatFiles);
      } else if (e.dataTransfer.files.length > 0) {
          const files = Array.from(e.dataTransfer.files);
          uploadFiles(files.map(f => ({ file: f, relativePath: f.name })));
      }
  };

  // Selection Handlers
  const handlePointerDown = (e: React.PointerEvent) => {
      if (e.button !== 0) return;
      setContextMenu(null); // Close context menu on any pointer down
      if ((e.target as HTMLElement).closest('.file-item')) return;
      if ((e.target as HTMLElement).clientWidth < (e.target as HTMLElement).scrollWidth) {
          // crude scrollbar check
      }
      
      isDragSelectingRef.current = false;
      
      if (!e.ctrlKey && !e.shiftKey) setSelectedPaths([]);
      
      selectionStartRef.current = { x: e.clientX, y: e.clientY };
      setSelectionBox({ x: e.clientX, y: e.clientY, w: 0, h: 0 });
      (e.target as HTMLElement).setPointerCapture(e.pointerId);

      // Cache Rects
      const items = document.querySelectorAll('.file-item');
      itemsRectCache.current = Array.from(items).map(el => ({
          path: el.getAttribute('data-path') || '',
          rect: el.getBoundingClientRect()
      }));
  };

  const handlePointerMove = (e: React.PointerEvent) => {
      if (!selectionStartRef.current) return;
      
      const currentX = e.clientX;
      const currentY = e.clientY;
      
      if (rafRef.current) cancelAnimationFrame(rafRef.current);

      rafRef.current = requestAnimationFrame(() => {
          const startX = selectionStartRef.current!.x;
          const startY = selectionStartRef.current!.y;
          
          if (Math.abs(currentX - startX) > 5 || Math.abs(currentY - startY) > 5) {
              isDragSelectingRef.current = true;
          }

          const x = Math.min(currentX, startX);
          const y = Math.min(currentY, startY);
          const w = Math.abs(currentX - startX);
          const h = Math.abs(currentY - startY);
          
          setSelectionBox({ x, y, w, h });
          
          const newSelection: string[] = [];
          for (const item of itemsRectCache.current) {
              const r = item.rect;
              if (x < r.right && x + w > r.left && y < r.bottom && y + h > r.top) {
                  newSelection.push(item.path);
              }
          }
          setSelectedPaths(newSelection);
      });
  };

  const handlePointerUp = (e: React.PointerEvent) => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      
      if (selectionStartRef.current) {
          selectionStartRef.current = null;
          setSelectionBox(null);
          (e.target as HTMLElement).releasePointerCapture(e.pointerId);
          itemsRectCache.current = [];
      }
  };
  
  const handleContainerClick = useCallback(() => {
      if (isDragSelectingRef.current) {
          isDragSelectingRef.current = false;
          return;
      }
      setSelectedPaths([]);
      setContextMenu(null);
  }, [setSelectedPaths]);

  // Track last selected index for shift-click range selection
  const lastSelectedIndexRef = useRef<number>(-1);

  // Memoized handlers for virtual list - must be before early return
  const handleItemClick = useCallback((path: string, e: React.MouseEvent) => {
    setContextMenu(null); // Close context menu when clicking a file
    const currentIndex = files.findIndex(f => f.path === path);

    if (e.shiftKey && lastSelectedIndexRef.current !== -1) {
      // Shift+click: select range
      const start = Math.min(lastSelectedIndexRef.current, currentIndex);
      const end = Math.max(lastSelectedIndexRef.current, currentIndex);
      const rangePaths = files.slice(start, end + 1).map(f => f.path);

      if (e.ctrlKey) {
        // Ctrl+Shift: add range to existing selection
        const newSelection = [...new Set([...selectedPaths, ...rangePaths])];
        setSelectedPaths(newSelection);
      } else {
        // Shift only: replace selection with range
        setSelectedPaths(rangePaths);
      }
    } else if (e.ctrlKey) {
      // Ctrl+click: toggle single item
      if (selectedPaths.includes(path)) {
        setSelectedPaths(selectedPaths.filter(p => p !== path));
      } else {
        setSelectedPaths([...selectedPaths, path]);
      }
      lastSelectedIndexRef.current = currentIndex;
    } else {
      // Normal click: select single item
      setSelectedPaths([path]);
      lastSelectedIndexRef.current = currentIndex;
    }
  }, [files, selectedPaths, setSelectedPaths]);

  const handleDoubleClick = useCallback((file: any) => {
    setContextMenu(null); // Close context menu on double click
    if (file.type === 'dir' || file.type === 'drive') {
      setCurrentPath(file.path);
    } else {
      openPreview(file);
    }
  }, [setCurrentPath, openPreview]);

  const handleContextMenu = useCallback((e: React.MouseEvent, path?: string) => {
    e.preventDefault();
    // Don't show context menu in "This PC" (root) view
    if (isRootView) return;

    if (path) {
      // Right-click on file/folder
      if (!selectedPaths.includes(path)) setSelectedPaths([path]);
    } else {
      // Right-click on empty area, clear selection
      setSelectedPaths([]);
    }

    setContextMenu({ x: e.clientX, y: e.clientY, path });
  }, [selectedPaths, setSelectedPaths, isRootView]);

  const handleDragStart = useCallback((event: any) => {
    setActiveDragFile(event.active.data.current?.file);
  }, []);

  // Memoized upload handler for virtual list
  const handleUploadFiles = useCallback((files: any[], target?: string) => {
    uploadFiles(files, target);
  }, [uploadFiles]);

  // Show loading state - but allow rendering files during streaming
  const showLoadingOverlay = isLoading && files.length === 0;
  const showStreamingIndicator = isLoading && files.length > 0 && hasMore;

  if (showLoadingOverlay) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-win-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Disk space analysis (treemap) view — self-contained, bypasses the list layout.
  if (viewMode === 'treemap') {
    return <TreemapView />;
  }

  const handleRenameSubmit = (oldPath: string, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      endRenaming(oldPath, e.currentTarget.value);
    } else if (e.key === 'Escape') {
        endRenaming(oldPath, oldPath.split('/').pop()!);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    setActiveDragFile(null);
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const srcFile = active.data.current?.file as FileStat;
    const destFile = over.data.current?.file as FileStat;

    if (srcFile && destFile && destFile.type === 'dir') {
       try {
         let filesToMove = [srcFile.path];
         
         if (selectedPaths.includes(srcFile.path)) {
             filesToMove = selectedPaths;
         }
         
         filesToMove = filesToMove.filter(p => p !== destFile.path);

         // Resolve Conflicts
         const ops = filesToMove.map(src => ({
             source: src,
             dest: joinPath(destFile.path, getBaseName(src))
         }));
         
         const finalOps = await checkConflictsBatch(ops);

         for (const op of finalOps) {
             await fs.move(op.source, op.dest);
         }
         await refresh();
         setSelectedPaths([]);
       } catch (e) {
         console.error('Move failed', e);
       }
    }
  };

  const isMultiDragging = activeDragFile && selectedPaths.includes(activeDragFile.path);

  // Use virtual scrolling for large directories
  const useVirtualScroll = files.length > VIRTUAL_SCROLL_THRESHOLD && viewMode !== 'details';

  // When using virtual scroll or details view, parent should not have overflow-auto (conflicts with react-window)
  // Details view handles its own virtual scrolling internally
  const needsVirtualContainer = useVirtualScroll || viewMode === 'details' || viewMode === 'list';
  const containerClassName = needsVirtualContainer
    ? `flex-1 overflow-hidden select-none h-full relative transition-colors ${isDragOver ? 'bg-blue-500/10' : ''}`
    : `flex-1 overflow-auto p-4 select-none h-full relative transition-colors scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent ${isDragOver ? 'bg-blue-500/10' : ''}`;

  return (
    <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <div
        className={containerClassName}
        onClick={handleContainerClick}
        onContextMenu={(e) => handleContextMenu(e)}
        onDragOver={handleNativeDragOver}
        onDragLeave={handleNativeDragLeave}
        onDrop={handleNativeDrop}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
      >
        {/* Selection Box */}
        {selectionBox && selectionBox.w > 0 && createPortal(
            <div 
                className="fixed bg-blue-500/20 border border-blue-500/50 z-[9999] pointer-events-none"
                style={{ 
                    left: selectionBox.x, 
                    top: selectionBox.y, 
                    width: selectionBox.w, 
                    height: selectionBox.h 
                }} 
            />,
            document.body
        )}

        {/* Special view for "This PC" - show drive cards */}
        {isRootView ? (
          <div className="p-4">
            <div className="text-xs text-white/50 mb-3">{t('devicesAndDrives')}</div>
            <div className="flex flex-wrap gap-3">
              {files.filter(f => f.type === 'drive').map(file => (
                <DriveCard
                  key={file.path}
                  file={file}
                  selected={selectedPaths.includes(file.path)}
                  driveSize={driveSizes[file.path.slice(0, 2)]}
                  onClick={(e) => { e.stopPropagation(); handleItemClick(file.path, e); }}
                  onDoubleClick={() => handleDoubleClick(file)}
                  onContextMenu={(e) => { e.stopPropagation(); handleContextMenu(e, file.path); }}
                />
              ))}
            </div>
          </div>
        ) : viewMode === 'details' ? (
           <FileDetailsView
             onContextMenu={(e, path) => handleContextMenu(e, path)}
             folderSizes={folderSizes}
             currentFolderSize={currentFolderSize}
             fetchFolderSizes={fetchFolderSizes}
             activeDragFile={activeDragFile}
             onDropFiles={handleUploadFiles}
           />
        ) : useVirtualScroll ? (
          /* Virtual scrolling for large directories */
          <VirtualFileList
            files={sortedFiles}
            viewMode={viewMode}
            selectedPaths={selectedPaths}
            renamingPath={renamingPath}
            activeDragFile={activeDragFile}
            folderSizes={folderSizes}
            currentFolderSize={currentFolderSize}
            fetchFolderSizes={fetchFolderSizes}
            onItemClick={handleItemClick}
            onItemDoubleClick={handleDoubleClick}
            onItemContextMenu={handleContextMenu}
            onDropFiles={handleUploadFiles}
            renderRenameInput={renderRenameInput}
          />
        ) : viewMode === 'grid' ? (
          /* Regular grid for small directories */
          <div className="grid grid-cols-[repeat(auto-fill,minmax(100px,1fr))] gap-2">
            {sortedFiles.map(file => {
              const isGhost = isMultiDragging && selectedPaths.includes(file.path) && file.path !== activeDragFile?.path;
              const folderSize = file.type === 'dir' ? folderSizes[file.path] : undefined;
              return (
              <FileItem
                key={file.path}
                file={file}
                selected={selectedPaths.includes(file.path)}
                renaming={renamingPath === file.path}
                isGhost={!!isGhost}
                viewMode="grid"
                folderSize={folderSize}
                parentFolderSize={currentFolderSize}
                isSearchEngineReady={isSearchEngineReady}
                searchEngineStatus={searchEngineStatus}
                openSearchEngineInitModal={openSearchEngineInitModal}
                onClick={(e) => { e.stopPropagation(); handleItemClick(file.path, e); }}
                onDoubleClick={() => handleDoubleClick(file)}
                onContextMenu={(e) => { e.stopPropagation(); handleContextMenu(e, file.path); }}
                onDropFiles={(files, target) => uploadFiles(files, target)}
              >
                {renderRenameInput(file)}
              </FileItem>
            );})}
          </div>
        ) : (
          /* Regular list for small directories */
          <div className="flex flex-col h-full">
            {/* List header */}
            <div className="flex items-center px-4 py-2 border-b border-white/10 text-xs text-white/50 bg-black/20 sticky top-0 z-10 shrink-0">
              <div className="flex-1 min-w-0">{t('name')}</div>
              <div className="w-20 text-right shrink-0">{t('size')}</div>
              <div className="w-28 text-right shrink-0 hidden sm:block">{t('dateModified')}</div>
              <div className="w-24 shrink-0 pl-2">
                <div className="flex flex-col">
                  <span className="text-[10px] text-white/40 mb-0.5">
                    {t('diskUsage')} {currentFolderSize ? `${currentFolderSize.percent.toFixed(1)}%` : '-'}
                  </span>
                  {currentFolderSize && (
                    <PercentBar percent={currentFolderSize.percent} showLabel={false} size="sm" />
                  )}
                </div>
              </div>
            </div>
            {/* File list */}
            <div className="flex-1 overflow-auto scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent">
              {sortedFiles.map(file => {
                const isGhost = isMultiDragging && selectedPaths.includes(file.path) && file.path !== activeDragFile?.path;
                const folderSize = file.type === 'dir' ? folderSizes[file.path] : undefined;
                return (
                <FileItem
                  key={file.path}
                  file={file}
                  selected={selectedPaths.includes(file.path)}
                  renaming={renamingPath === file.path}
                  isGhost={!!isGhost}
                  viewMode="list"
                  folderSize={folderSize}
                  parentFolderSize={currentFolderSize}
                  isSearchEngineReady={isSearchEngineReady}
                  searchEngineStatus={searchEngineStatus}
                  openSearchEngineInitModal={openSearchEngineInitModal}
                  onClick={(e) => { e.stopPropagation(); handleItemClick(file.path, e); }}
                  onDoubleClick={() => handleDoubleClick(file)}
                  onContextMenu={(e) => { e.stopPropagation(); handleContextMenu(e, file.path); }}
                  onDropFiles={(files, target) => uploadFiles(files, target)}
                >
                   {renderRenameInput(file)}
                </FileItem>
              );})}
            </div>
          </div>
        )}

        {/* Drag Overlay for visual feedback */}
        {createPortal(
            <DragOverlay>
            {activeDragFile ? (
                <div className="opacity-80 scale-105 pointer-events-none relative">
                    <div className="bg-win-accent text-white px-3 py-1 rounded shadow-lg flex items-center">
                        {selectedPaths.includes(activeDragFile.path) && selectedPaths.length > 1 ? (
                            <>
                                <span className="text-sm font-medium mr-2">{t('movingItems', { count: selectedPaths.length })}</span>
                                <div className="absolute -top-2 -right-2 bg-red-500 text-white text-[10px] font-bold w-5 h-5 flex items-center justify-center rounded-full border-2 border-[#202020]">
                                    {selectedPaths.length}
                                </div>
                            </>
                        ) : (
                            <span className="text-sm font-medium">{activeDragFile.name}</span>
                        )}
                    </div>
                </div>
            ) : null}
            </DragOverlay>,
            document.body
        )}

        {contextMenu && (
          <ContextMenu
            x={contextMenu.x}
            y={contextMenu.y}
            targetPath={contextMenu.path}
            onClose={() => setContextMenu(null)}
          />
        )}

        {/* Streaming progress indicator */}
        {showStreamingIndicator && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/80 text-white px-4 py-2 rounded-full flex items-center gap-2 shadow-lg z-10">
            <div className="w-4 h-4 border-2 border-win-accent border-t-transparent rounded-full animate-spin" />
            <span className="text-sm">
              {t('loadingFiles', { loaded: files.length, total: totalFiles })}
            </span>
          </div>
        )}

        <PreviewModal />
        <EditModal />
      </div>
    </DndContext>
  );
};

export default MainView;
